/**
 * LocalStorage utilities for message history
 */
const STORAGE_KEY = 'nova-chat-history';
const SESSION_KEY = 'nova-chat-session-id';
const RECENT_CHATS_KEY = 'nova-chat-recent';
const SESSION_SNAPSHOT_PREFIX = 'nova-chat-session-';
const LAST_KPI_PREFIX = 'nova-last-kpi-question-';

export function saveLastKpiQuestion(sessionId, question) {
  if (!sessionId || !question) return;
  if (!/\bkpi\b/i.test(question)) return;
  try {
    localStorage.setItem(`${LAST_KPI_PREFIX}${sessionId}`, question);
  } catch (error) {
    console.error('Error saving last KPI question:', error);
  }
}

export function loadLastKpiQuestion(sessionId) {
  if (!sessionId) return null;
  try {
    return localStorage.getItem(`${LAST_KPI_PREFIX}${sessionId}`);
  } catch (error) {
    console.error('Error loading last KPI question:', error);
    return null;
  }
}

export function buildKpiHistory(sessionId, messages = []) {
  const fromMessages = (Array.isArray(messages) ? messages : [])
    .filter((message) => message.sender === 'user' && typeof message.content === 'string')
    .map((message) => message.content);
  return [...new Set([loadLastKpiQuestion(sessionId), ...fromMessages].filter(Boolean))].slice(-8);
}

export function getSessionId() {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 16);
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function clearSessionId() {
  localStorage.removeItem(SESSION_KEY);
}

export function setStoredSessionId(sessionId) {
  localStorage.setItem(SESSION_KEY, sessionId);
}

export function generateNewSessionId() {
  clearSessionId();
  return getSessionId();
}

export function saveMessageHistory(messages) {
  try {
    // Keep only last 50 messages to prevent localStorage overflow
    const historyToSave = messages.slice(-50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(historyToSave));
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
}

export function loadMessageHistory() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.error('Error loading chat history:', error);
  }
  return [];
}

export function clearMessageHistory() {
  localStorage.removeItem(STORAGE_KEY);
}

export function loadRecentChats() {
  try {
    const saved = localStorage.getItem(RECENT_CHATS_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Error loading recent chats:', error);
    return [];
  }
}

export function saveRecentChats(chats) {
  try {
    localStorage.setItem(RECENT_CHATS_KEY, JSON.stringify(chats.slice(0, 12)));
  } catch (error) {
    console.error('Error saving recent chats:', error);
  }
}

export function saveSessionSnapshot(sessionId, data) {
  try {
    localStorage.setItem(`${SESSION_SNAPSHOT_PREFIX}${sessionId}`, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving session snapshot:', error);
  }
}

export function loadSessionSnapshot(sessionId) {
  try {
    const saved = localStorage.getItem(`${SESSION_SNAPSHOT_PREFIX}${sessionId}`);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error loading session snapshot:', error);
    return null;
  }
}

export function removeSessionSnapshot(sessionId) {
  try {
    localStorage.removeItem(`${SESSION_SNAPSHOT_PREFIX}${sessionId}`);
  } catch (error) {
    console.error('Error removing session snapshot:', error);
  }
}

/**
 * Open a brand-new chat on the next visit to /chat (called after sign-in).
 * The chat that was open is kept: if it has a question, it is saved as a snapshot
 * and listed in Recent so the user can reopen it.
 */
export function startFreshChat() {
  try {
    const sessionId = localStorage.getItem(SESSION_KEY);
    const messages = loadMessageHistory();
    const firstQuestion = messages.find((m) => m.sender === 'user' && typeof m.content === 'string');
    if (sessionId && firstQuestion) {
      const text = firstQuestion.content.trim();
      const title = text.length > 42 ? `${text.slice(0, 42)}…` : text;
      const updatedAt = Date.now();
      saveSessionSnapshot(sessionId, { title, messages, updatedAt });
      const recents = loadRecentChats().filter((chat) => chat.id !== sessionId);
      saveRecentChats([{ id: sessionId, title, updatedAt }, ...recents]);
    }
  } catch (error) {
    console.error('Error saving the previous chat:', error);
  }
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Clear all Nova-Bot related data from localStorage
 * Useful for debugging or forcing a fresh start
 */
export function clearAllNovaBotData() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('authToken');
    localStorage.removeItem('welcome-message-updated');
    console.log('All Nova-Bot data cleared from localStorage');
    return true;
  } catch (error) {
    console.error('Error clearing Nova-Bot data:', error);
    return false;
  }
}

/**
 * Force browser to reload and clear HTTP cache
 * This will trigger a hard reload, bypassing cache
 */
export function forceReload() {
  // Clear localStorage first
  clearAllNovaBotData();
  // Force reload bypassing cache
  window.location.reload(true);
  // Fallback for modern browsers
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    });
  }
}

